import Button from 'assets/components/button/Button';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Icon from 'assets/components/icon/Icon';
import { getLocales } from 'assets/locales/Locale';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { OrderType } from 'config/Api.Config';
import React from 'react';
import { useLossCodeProvider } from '../LossCode.Hooks';
import ThemeFilterFormDrawer from 'assets/components/filters/filterForm/themes/Theme.FilterForm.Drawer';
import FilterForm from 'assets/components/filters/filterForm/FilterForm';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import LossCodeSection from 'models/enums/LossCodeSection';

enum LossCodeFlagOption {
  internal = 'internal',
  external = 'external',
  disabled = 'disabled',
  active = 'active',
}

function flagToOption(flag: boolean, whenTrue: LossCodeFlagOption, whenFalse: LossCodeFlagOption) {
  return flag === true ? whenTrue : flag === false ? whenFalse : undefined;
}

function optionToFlag(option: string, whenTrue: LossCodeFlagOption, whenFalse: LossCodeFlagOption) {
  return option === whenTrue ? true : option === whenFalse ? false : undefined;
}

export default function LossCodeFilters() {
  const { lang } = getLocales();
  const { listApi } = useLossCodeProvider();

  return (
    <FilterForm
      subcomponents={ThemeFilterFormDrawer}
      search={listApi.body?.search}
      onSearch={(search) => listApi.execute((body) => ({ ...body, search, offset: 0 }))}
      filters={listApi.body}
      onFilter={(newFilers) => listApi.execute({ ...newFilers, offset: 0 })}
      onReset={listApi.reset}
      render={({ tempFilters, setFilters }) => (
        <>
          <SelectInput
            name="lossCodeSectionCode"
            label={lang.lossCodeSection}
            data={[
              { id: LossCodeSection.part, title: lang.part },
              { id: LossCodeSection.labour, title: lang.labour },
              { id: LossCodeSection.other, title: lang.otherSublet },
            ]}
            isMultiselect
            value={tempFilters?.lossCodeSectionCode}
            onChange={({ lossCodeSectionCode }) => setFilters({ ...tempFilters, lossCodeSectionCode })}
            icon={<Icon class="fas-layer-group" />}
          />
          <SelectInput
            name="isInternalOnly"
            label={lang.lossCodeVisibility}
            data={[
              { id: LossCodeFlagOption.internal, title: lang.internalOnly },
              { id: LossCodeFlagOption.external, title: lang.visibleExternally },
            ]}
            value={flagToOption(tempFilters?.isInternalOnly, LossCodeFlagOption.internal, LossCodeFlagOption.external)}
            onChange={({ isInternalOnly }) =>
              setFilters({
                ...tempFilters,
                isInternalOnly: optionToFlag(isInternalOnly, LossCodeFlagOption.internal, LossCodeFlagOption.external),
              })
            }
            icon={<Icon class="fas-eye" />}
          />
          <SelectInput
            name="isDisabled"
            label={lang.status}
            data={[
              { id: LossCodeFlagOption.active, title: lang.active },
              { id: LossCodeFlagOption.disabled, title: lang.disabled },
            ]}
            value={flagToOption(tempFilters?.isDisabled, LossCodeFlagOption.disabled, LossCodeFlagOption.active)}
            onChange={({ isDisabled }) =>
              setFilters({
                ...tempFilters,
                isDisabled: optionToFlag(isDisabled, LossCodeFlagOption.disabled, LossCodeFlagOption.active),
              })
            }
            icon={<Icon class="fas-power-off" />}
          />
        </>
      )}
      headerActions={
        <DropdownButton
          buttonMedia={<Icon class="fas-sort" />}
          horizontalPosition="auto_fixed"
          verticalPosition="auto_fixed"
          htmlElementProps={{ title: lang.sort }}
          subcomponents={ThemeDropdownButtonPopup}
          dropdownOptions={
            <>
              <Button
                class={ThemeButtonMenuItem}
                render={lang.newerFirst}
                media={<Icon class="fas-caret-up" />}
                onClick={listApi.execute}
                data={(currentFilters) => ({
                  ...currentFilters,
                  orderBy: 'id',
                  orderType: OrderType.desc,
                })}
              />
              <Button
                class={ThemeButtonMenuItem}
                render={lang.olderFirst}
                media={<Icon class="fas-caret-down" />}
                onClick={listApi.execute}
                data={(currentFilters) => ({
                  ...currentFilters,
                  orderBy: 'id',
                  orderType: OrderType.asc,
                })}
              />
              <Button
                class={ThemeButtonMenuItem}
                render={lang.ascendingByCode}
                media={<Icon class="fas-caret-up" />}
                onClick={listApi.execute}
                data={(currentFilters) => ({
                  ...currentFilters,
                  orderBy: 'code',
                  orderType: OrderType.asc,
                })}
              />
              <Button
                class={ThemeButtonMenuItem}
                render={lang.descendingByCode}
                media={<Icon class="fas-caret-down" />}
                onClick={listApi.execute}
                data={(currentFilters) => ({
                  ...currentFilters,
                  orderBy: 'code',
                  orderType: OrderType.desc,
                })}
              />
            </>
          }
        />
      }
    />
  );
}
